import { useEffect, useSyncExternalStore } from "react";

type Theme = "light" | "dark" | "auto";

// Тема — общее состояние: хук вызывается и в шапке (Shell), и в настройках,
// обе точки должны видеть одно значение. Простейший внешний стор + подписка.
// localStorage в try/catch: модуль-скоуп, брошенное исключение (приватный
// режим/заблокированный storage) валило бы всё приложение белым экраном.
function readStored(): Theme {
  try {
    return (localStorage.getItem("theme") as Theme) || "auto";
  } catch {
    return "auto";
  }
}
let current: Theme = readStored();
const listeners = new Set<() => void>();

function setTheme(next: Theme) {
  current = next;
  try {
    localStorage.setItem("theme", next);
  } catch {
    // storage недоступен — тема поживёт до перезагрузки
  }
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

// Тёмная ли тема ПРЯМО СЕЙЧАС — для мест, где цвет нельзя задать классом
// (SVG-пропсы recharts). Слушаем сам класс на <html>: его ставит useTheme в
// Shell, и режим "auto" меняет его без изменения значения темы.
function subscribeDark(l: () => void) {
  const mo = new MutationObserver(l);
  mo.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"],
  });
  return () => mo.disconnect();
}

export function useIsDark(): boolean {
  return useSyncExternalStore(
    subscribeDark,
    () => document.documentElement.classList.contains("dark"),
    () => false,
  );
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, () => current);

  useEffect(() => {
    const apply = () => {
      const sysDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      const dark = theme === "dark" || (theme === "auto" && sysDark);
      document.documentElement.classList.toggle("dark", dark);
    };
    apply();
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const listener = () => theme === "auto" && apply();
    mq.addEventListener("change", listener);
    return () => mq.removeEventListener("change", listener);
  }, [theme]);

  return { theme, setTheme };
}
